import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import {
  bulkAddVouchers,
  createFlightCompany,
  listFlightCompanies,
  listVouchers,
} from '../services/centralDbClient';
import type { FlightCompanyOption, TaronVoucher } from '../services/centralDbClient';

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const dtf = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  return dtf.format(d);
};

export default function VouchersPage() {
  const theme = useTheme();

  const [items, setItems] = useState<TaronVoucher[]>([]);
  const [input, setInput] = useState('');
  const [expiry, setExpiry] = useState<string>(''); // yyyy-mm-dd
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<'created' | 'expiry' | 'code'>('created');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound'>('');
  const [companies, setCompanies] = useState<FlightCompanyOption[]>([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const [newCompanyCode, setNewCompanyCode] = useState('');

  const loadCompanies = useCallback(async () => {
    const list = await listFlightCompanies();
    setCompanies(list);
    if (list.length === 0) {
      setSelectedCompanyCode('');
      return;
    }
    if (selectedCompanyCode && !list.some((company) => company.code === selectedCompanyCode)) {
      setSelectedCompanyCode(list[0].code);
    }
  }, [selectedCompanyCode]);

  useEffect(() => {
    (async () => {
      const v = await listVouchers();
      setItems(v);
    })();
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const parsedLines = useMemo(() => {
    const raw = input.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const uniq: string[] = Array.from(new Set(raw));
    // Enforce global uniqueness across inbound/outbound: a code can't exist in either direction already
    const existingCodes: Set<string> = new Set(
      items.map((i) => (i.code ?? '').toUpperCase())
    );
    const newCodes = uniq.filter((c) => !existingCodes.has(c.toUpperCase()));
    const duplicates = uniq.filter((c) => existingCodes.has(c.toUpperCase()));
    return { newCodes, duplicates };
  }, [input, items]);

  const filteredSorted = useMemo(() => {
    const f = items.filter((it) => (filter ? it.code.toLowerCase().includes(filter.toLowerCase()) : true));
    const arr = [...f];
    if (sortBy === 'created') arr.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    if (sortBy === 'expiry') arr.sort((a, b) => {
      const ax = a.expiryDate ?? '9999-12-31';
      const bx = b.expiryDate ?? '9999-12-31';
      return ax.localeCompare(bx) || b.createdAt.localeCompare(a.createdAt);
    });
    if (sortBy === 'code') arr.sort((a, b) => a.code.localeCompare(b.code));
    return arr;
  }, [items, filter, sortBy]);

  const handleAdd = async () => {
    const { newCodes } = parsedLines;
    if (newCodes.length === 0) return;
    if (!direction) return; // guard; button will be disabled
    const priceValue = defaultPrice.trim() ? Number(defaultPrice) : undefined;
    await bulkAddVouchers(
      newCodes,
      expiry || undefined,
      direction,
      selectedCompanyCode || undefined,
      Number.isFinite(priceValue) ? priceValue : undefined,
    );
    const v = await listVouchers();
    setItems(v);
    setInput('');
    setSelected(new Set());
    setDirection('');
    setDefaultPrice('');
  };

  const handleCreateCompany = async () => {
    const code = newCompanyCode.trim().toUpperCase();
    if (!code) return;
    await createFlightCompany({ code });
    setNewCompanyCode('');
    await loadCompanies();
    setSelectedCompanyCode(code);
  };

  const handleDelete = async (id: string) => {
    // server doesn't expose delete yet; just filter out locally
    setItems(prev => prev.filter(x => x.id !== id));
    setSelected(s => { const ns = new Set(s); ns.delete(id); return ns; });
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setItems(prev => prev.filter(x => !selected.has(x.id)));
    setSelected(new Set());
  };

  const handleClearAll = async () => {
    if (!confirm('Clear all vouchers? This cannot be undone.')) return;
    // no bulk delete; emulate by clearing view
    setItems([]);
    setSelected(new Set());
  };

  const allSelected = selected.size > 0 && filteredSorted.every((it) => selected.has(it.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredSorted.map((x) => x.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Vouchers</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="sort-by">Sort by</InputLabel>
            <Select labelId="sort-by" label="Sort by" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <MenuItem value="created">Created (newest)</MenuItem>
              <MenuItem value="expiry">Expiry date</MenuItem>
              <MenuItem value="code">Code</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Filter codes" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <Button size="small" variant="outlined" color="error" onClick={handleClearAll}>Clear all</Button>
        </Box>
      </Box>

      <Box sx={{ px: 2, mb: 2 }}>
        <Paper elevation={0} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`, borderRadius: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Add vouchers</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Paste voucher codes here, one per line"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              InputProps={{ startAdornment: <ContentPasteIcon color="disabled" sx={{ mr: 1 }} /> as any }}
            />
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 260, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="voucher-company">Flight company</InputLabel>
                <Select
                  labelId="voucher-company"
                  label="Flight company"
                  value={selectedCompanyCode}
                  onChange={(e) => setSelectedCompanyCode(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.code} value={company.code}>
                      {company.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as any)}
                >
                  <FormControlLabel value="inbound" control={<Radio />} label="Inbound" />
                  <FormControlLabel value="outbound" control={<Radio />} label="Outbound" />
                </RadioGroup>
              </FormControl>
              <TextField
                label="Expiry date"
                type="date"
                size="small"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Default price (€)"
                type="number"
                size="small"
                value={defaultPrice}
                onChange={(e) => setDefaultPrice(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <Button variant="contained" onClick={handleAdd} disabled={parsedLines.newCodes.length === 0 || !direction}>Add</Button>
            </Stack>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
            <TextField
              label="Add flight company"
              placeholder="Enter code"
              size="small"
              value={newCompanyCode}
              onChange={(e) => setNewCompanyCode(e.target.value)}
              sx={{ maxWidth: 220 }}
            />
            <Button variant="outlined" onClick={handleCreateCompany} disabled={!newCompanyCode.trim()}>Add company</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {parsedLines.newCodes.length} new code(s) will be added{parsedLines.duplicates.length ? `, ${parsedLines.duplicates.length} duplicate(s) skipped` : ''}.{!direction ? ' Select inbound or outbound to proceed.' : ''}
          </Typography>
        </Paper>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        borderRadius: 1.5,
        overflow: 'auto',
        mx: 2,
      }}>
        <Table size="small" sx={{ minWidth: 900, width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.06) }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && !allSelected}
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Default price</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Assigned to</TableCell>
              <TableCell align="right">
                <Tooltip title={selected.size ? `Delete ${selected.size} selected` : 'Delete selected'}>
                  <span>
                    <IconButton color="error" disabled={selected.size === 0} onClick={handleDeleteSelected} size="small">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSorted.map((it) => (
              <TableRow key={it.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{it.code}</TableCell>
                <TableCell>{it.direction ? (it.direction === 'inbound' ? 'Inbound' : 'Outbound') : '—'}</TableCell>
                <TableCell>{it.companyCode || '—'}</TableCell>
                <TableCell>{typeof it.defaultPrice === 'number' ? `€ ${it.defaultPrice.toFixed(2)}` : '—'}</TableCell>
                <TableCell>{it.expiryDate || '—'}</TableCell>
                <TableCell>{formatDate(it.createdAt)}</TableCell>
                <TableCell>
                  {it.assignedTravelId
                    ? `${it.assignedPersonName || ''}${it.assignedFlightDate ? ` — ${it.assignedFlightDate}` : ''}`
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => handleDelete(it.id)} size="small">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredSorted.length === 0 && (
              <TableRow>
                <TableCell align="center" sx={{ py: 3 }} {...({ colSpan: 9 } as any)}>
                  <Typography variant="body2" color="text.secondary">
                    No vouchers yet. Paste codes above to add.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
